import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { unwrap, unwrapArray } from '../../lib/api-envelope';
import { DollarSign, Truck, Package, FileText, Search } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { tenantTodayDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency } from '../../lib/display-labels';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import PageHeaderBand, { type PageHeaderStat } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../../lib/load-failure';

/** Whole-dollar money. @see fmtCurrency — absence renders '—', not '$0'. */
const fmt = (n: number | string | null | undefined) => fmtCurrency(n, null, 0);

/**
 * The saturated badge idiom: a `--fi-signal-*` FILL with `text-fi-signal-ink`.
 *
 * These were pale 10%-ish washes with the hue as the INK (`text-blue-400` on
 * `bg-blue-500/20`), which is the failure CLAUDE.md spells out: a signal hue has
 * no light twin, so as text it keeps its bright dark-mode value on a white
 * panel. The fill does not change with the theme, so one dark ink holds in both.
 *
 * FIVE HUES, NO SIXTH IS ADDED. `rental` was purple and there is no purple in
 * the palette; it shares slate with the other non-trucking marks, and the label
 * tells them apart. An unmapped status falls to slate and renders its own text.
 */
const BADGE_TONE: Record<string, string> = {
  trucking: 'bg-fi-signal-blue',
  rental: 'bg-fi-signal-slate',
  green: 'bg-fi-signal-green',
  amber: 'bg-fi-signal-amber',
  red: 'bg-fi-signal-red',
  blue: 'bg-fi-signal-blue',
};

function Badge({ label, variant }: { label: string; variant: 'trucking' | 'rental' | 'green' | 'amber' | 'red' | 'blue' }) {
  const tone = BADGE_TONE[variant] ?? 'bg-fi-signal-slate';
  return (
    <span
      className={`inline-flex items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-fi-signal-ink ${tone}`}
    >
      {label}
    </span>
  );
}

function statusVariant(s: string): 'green' | 'amber' | 'red' | 'blue' {
  const lower = (s || '').toLowerCase();
  if (lower.includes('paid') || lower.includes('approved')) return 'green';
  if (lower.includes('pending') || lower.includes('draft')) return 'amber';
  if (lower.includes('overdue') || lower.includes('cancelled')) return 'red';
  return 'blue';
}

/** A body panel in the register idiom. */
function Panel({
  eyebrow,
  title,
  icon,
  rail,
  children,
  footer,
}: {
  eyebrow: string;
  title: string;
  icon?: React.ReactNode;
  rail?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <section
      className={`overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card ${
        rail ? 'border-l-4 border-l-fi-amber' : ''
      }`}
    >
      <header className="border-b border-fieldiq-border px-4 py-4">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
        <h2 className="mt-1 flex items-center gap-2 text-base font-extrabold tracking-tight text-fieldiq-text">
          {icon}
          {title}
        </h2>
      </header>
      <div className="px-4 py-4">{children}</div>
      {footer && (
        <div className="border-t border-fieldiq-border px-4 py-3 text-[11px] text-fieldiq-muted">{footer}</div>
      )}
    </section>
  );
}

export default function CustomerReportPage() {
  const navigate = useNavigate();
  // The TENANT's current month (companies.time_zone), not the device's. No
  // zone, no seeded range — the inputs start blank rather than guess.
  const timeZone = useTenantTimeZone();
  const now = tenantTodayDate(timeZone);
  const [startDate, setStartDate] = useState(now ? format(startOfMonth(now), 'yyyy-MM-dd') : '');
  const [endDate, setEndDate] = useState(now ? format(endOfMonth(now), 'yyyy-MM-dd') : '');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch customer list
  const customersQuery = useQuery({
    queryKey: ['report-customers'],
    queryFn: () => apiClient.get('/reports/customers').then(r => unwrapArray<any>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: customerData, isLoading: customersLoading } = customersQuery;
  const customersFailure = queryLoadFailure(customersQuery);
  const customers: any[] = Array.isArray(customerData) ? customerData : [];

  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers;
    const q = searchQuery.toLowerCase();
    return customers.filter((c: any) => (c.name || '').toLowerCase().includes(q));
  }, [customers, searchQuery]);

  // Fetch customer detail report
  const reportQuery = useQuery({
    queryKey: ['customer-report', selectedCustomerId, startDate, endDate],
    queryFn: () => apiClient.get(`/reports/customer/${selectedCustomerId}`, { params: { start_date: startDate, end_date: endDate } }).then(r => unwrap<any>(r.data)),
    enabled: !!selectedCustomerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: report, isLoading: reportLoading } = reportQuery;
  const reportFailure = selectedCustomerId ? queryLoadFailure(reportQuery) : null;

  const selectedCustomer = customers.find((c: any) => c.id === selectedCustomerId);

  // GET /reports/customer/:id returns { customer, period, summary, trucking,
  // rentals } — every figure below used to be read off the top-level object,
  // where none of them exist, so the whole report rendered $0 / 0 / empty.
  const summary = report?.summary;
  const trucking = report?.trucking;
  const rentals = report?.rentals;
  const truckingRevenue = trucking?.revenue ?? summary?.trucking_revenue ?? 0;
  const rentalRevenue = rentals?.revenue ?? summary?.rental_revenue ?? 0;
  // The API returns no combined invoice list for this endpoint (see the note on
  // the timeline section below), so absent and empty must not look alike.
  const invoices: any[] | null = Array.isArray(report?.invoices) ? report.invoices : null;

  /**
   * FOUR STATS, ALL FOUR REAL — and all four are em dashes until a customer is
   * picked, which is correct rather than awkward: nothing has been asked for
   * yet, so there is no figure, and `fmtCurrency(undefined)` says so.
   *
   * ORDER IS THE CIRCLE'S. The third slot's label has a ~70px ceiling
   * (CLAUDE.md, fe#698). "Rental Revenue" measures 112.4px in this slot's type
   * and "Trucking Revenue" 124.0px, so the two money splits carry their short
   * names — "Trucking" 64.9px, "Rentals" 55.8px — and the SUB-LINES say that
   * each is revenue. The body's own panels keep the long names, which is
   * CLAUDE.md's rule exactly: shorten the stat, leave the page's names alone.
   *
   * `?? 0` IS NOT REPRODUCED HERE. The two revenue consts above coalesce to 0
   * because the body's "no activity this period" branches compare against 0 —
   * behaviour this PR does not touch — but the band reads the payload's own
   * keys, so a report that arrives without them renders an em dash instead of
   * claiming the customer billed nothing.
   */
  const bandStats = useMemo((): PageHeaderStat[] => [
    {
      icon: <DollarSign className="size-4" />,
      value: fmt(summary?.total_revenue),
      label: 'Total Revenue',
      sub: 'Trucking and rentals',
      tone: 'accent',
      loading: reportLoading,
    },
    {
      icon: <Truck className="size-4" />,
      value: fmt(trucking?.revenue ?? summary?.trucking_revenue),
      label: 'Trucking',
      sub: 'Revenue on loads',
      tone: 'info',
      loading: reportLoading,
    },
    {
      icon: <Package className="size-4" />,
      value: fmt(rentals?.revenue ?? summary?.rental_revenue),
      label: 'Rentals',
      sub: 'Revenue on orders',
      loading: reportLoading,
    },
    {
      icon: <FileText className="size-4" />,
      value: displayValue(summary?.total_invoices),
      label: 'Invoices',
      sub: 'In this period',
      loading: reportLoading,
    },
  ], [summary, trucking, rentals, reportLoading]);

  return (
    <div data-testid="cross-customer-report-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Reports"
        title="Cross-Customer Report"
        /* NO `description`, AND IT WAS MEASURED OUT RATHER THAN LEFT OFF BY
           TASTE. A description line is 32px tall (24px of text plus its 8px
           margin), and it pushes the stat strip from 109px below the band's top
           to 141px — down into the widest part of the hero circle, whose
           clearance was swept against Quotes' and Rate Sheets' STAT TEXT ONLY,
           at their height. The sweep for this PR read the fourth sub-line
           ("In this period") at **3.28 dark / 3.37 light**, under the floor at
           160 of the 193 widths from 1024 to 2560. Removing the line put the
           strip back at 109 and the page clear.

           The sentence itself was not lost: it is the eyebrow over the picker
           in the body, where the reader meets it at the moment they need it.
           Neither reference page uses a description either. */
        onBack={() => navigate('/reports')}
        backLabel="Back to reports"
        stats={bandStats}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* The picker, in the body under the band. Its heading carries the
              sentence that used to be the band's description. */}
          <section aria-labelledby="cross-customer-picker-title" className="space-y-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4">
            <div>
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                Combined trucking and rental activity
              </p>
              <h2 id="cross-customer-picker-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Pick a customer
              </h2>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search customers..."
                aria-label="Search customers"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark pl-9 pr-4 text-sm text-fieldiq-text placeholder-fieldiq-muted focus:outline-none focus:ring-1 focus:ring-fi-amber"
              />
            </div>

            {!selectedCustomerId && (
              <div className="max-h-60 space-y-1.5 overflow-y-auto">
                {/* AN ERROR IS NOT AN EMPTY STATE. "No customers found." stood
                    alone here, so a refused or unreachable list read as a tenant
                    with no customers — on the page whose whole first step is
                    picking one. */}
                {customersFailure ? (
                  <LoadFailureNotice
                    error={queryFailureError(customersQuery)}
                    kind={customersFailure}
                    subject="customers"
                    allowed="admins and office staff"
                  />
                ) : customersLoading ? (
                  <span
                    role="status"
                    aria-label="Loading customers"
                    className="block h-10 w-full animate-pulse rounded-fi-control bg-fieldiq-border"
                  />
                ) : filteredCustomers.length === 0 ? (
                  <p className="py-4 text-center text-sm text-fieldiq-muted">No customers found.</p>
                ) : (
                  filteredCustomers.map((c: any) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => { setSelectedCustomerId(c.id); setSearchQuery(''); }}
                      className="flex w-full items-center justify-between rounded-fi-control border border-fieldiq-border bg-fieldiq-dark p-3 text-left transition hover:border-fi-amber/50"
                    >
                      <div>
                        <p className="text-sm font-semibold text-fieldiq-text">{c.name}</p>
                        <div className="mt-1 flex gap-1.5">
                          {(c.has_trucking || c.verticals?.includes('trucking')) && <Badge label="Trucking" variant="trucking" />}
                          {(c.has_rentals || c.verticals?.includes('surface_rentals')) && <Badge label="Rentals" variant="rental" />}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {selectedCustomerId && (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedCustomerId(null)}
                  className="text-xs font-semibold text-fieldiq-accent hover:underline"
                >
                  Change customer
                </button>
                <span className="text-sm font-semibold text-fieldiq-text">{selectedCustomer?.name}</span>
                <div className="ml-auto flex items-center gap-2">
                  <input
                    type="date"
                    aria-label="Start date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="h-9 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:outline-none focus:ring-1 focus:ring-fi-amber"
                  />
                  <span className="text-xs text-fieldiq-muted">to</span>
                  <input
                    type="date"
                    aria-label="End date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="h-9 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:outline-none focus:ring-1 focus:ring-fi-amber"
                  />
                </div>
              </div>
            )}
          </section>

          {/* AN ERROR IS NOT AN EMPTY STATE — the detail read, separately from
              the list read, because either can fail on its own. */}
          {selectedCustomerId && !reportLoading && reportFailure && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice
                error={queryFailureError(reportQuery)}
                kind={reportFailure}
                subject={`this customer's report`}
                allowed="admins and office staff"
              />
            </div>
          )}

          {selectedCustomerId && report && !reportLoading && (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <Panel
                  eyebrow="Trucking"
                  title="Trucking Activity"
                  rail
                  icon={<Truck className="size-4 text-fieldiq-accent" aria-hidden="true" />}
                >
                  {truckingRevenue === 0 ? (
                    <p className="py-6 text-center text-sm text-fieldiq-muted">No trucking activity this period</p>
                  ) : (
                    <>
                      <div className="mb-3 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Loads</p>
                          <p className="mt-1 font-display text-lg font-extrabold tracking-tight text-fieldiq-text">{displayValue(trucking?.total_loads)}</p>
                        </div>
                        <div>
                          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Revenue</p>
                          <p className="mt-1 font-display text-lg font-extrabold tracking-tight text-fieldiq-text">{fmt(truckingRevenue)}</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[420px] text-left text-xs">
                          <thead>
                            <tr className="border-b border-fieldiq-border">
                              <th scope="col" className="py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Date</th>
                              <th scope="col" className="py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Load #</th>
                              <th scope="col" className="py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Route</th>
                              <th scope="col" className="py-2 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(trucking?.recent_loads || []).slice(0, 10).map((l: any, i: number) => (
                              <tr key={i} className="border-b border-fieldiq-border last:border-b-0">
                                <td className="py-1.5 text-fieldiq-muted">{l.scheduled_date || l.date || '—'}</td>
                                <td className="py-1.5 text-fieldiq-text">{l.bol_number || l.load_number || '—'}</td>
                                <td className="py-1.5 text-fieldiq-muted">{`${l.pickup_location || '—'} → ${l.delivery_location || '—'}`}</td>
                                <td className="py-1.5 text-right tabular-nums text-fieldiq-text">{l.amount == null ? '—' : fmt(l.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </Panel>

                <Panel
                  eyebrow="Rentals"
                  title="Rental Activity"
                  icon={<Package className="size-4 text-fieldiq-accent" aria-hidden="true" />}
                >
                  {rentalRevenue === 0 ? (
                    <p className="py-6 text-center text-sm text-fieldiq-muted">No rental activity this period</p>
                  ) : (
                    <>
                      <div className="mb-3 grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Orders</p>
                          <p className="mt-1 font-display text-lg font-extrabold tracking-tight text-fieldiq-text">{displayValue(rentals?.total_orders)}</p>
                        </div>
                        <div>
                          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Assets</p>
                          <p className="mt-1 font-display text-lg font-extrabold tracking-tight text-fieldiq-text">{displayValue(rentals?.assets_on_rent)}</p>
                        </div>
                        <div>
                          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Revenue</p>
                          <p className="mt-1 font-display text-lg font-extrabold tracking-tight text-fieldiq-text">{fmt(rentalRevenue)}</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[420px] text-left text-xs">
                          <thead>
                            <tr className="border-b border-fieldiq-border">
                              <th scope="col" className="py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Order #</th>
                              <th scope="col" className="py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Well/Site</th>
                              <th scope="col" className="py-2 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Days</th>
                              <th scope="col" className="py-2 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(rentals?.recent_orders || []).slice(0, 10).map((o: any, i: number) => (
                              <tr key={i} className="border-b border-fieldiq-border last:border-b-0">
                                <td className="py-1.5 text-fieldiq-text">{o.order_number}</td>
                                <td className="py-1.5 text-fieldiq-muted">{o.well_name || '—'}</td>
                                <td className="py-1.5 text-right tabular-nums text-fieldiq-muted">{o.days_on_rent ?? '—'}</td>
                                <td className="py-1.5 text-right tabular-nums text-fieldiq-text">{o.amount == null ? '—' : fmt(o.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </Panel>
              </div>

              <Panel eyebrow="Billing" title="Combined Invoice Timeline">
                {/* GET /reports/customer/:id returns trucking.invoices and
                    rentals.invoices as COUNTS, and no combined invoice list. This
                    table has therefore never had rows. Saying "no invoices this
                    period" when the server sent {summary?.total_invoices} of them
                    is the same lie as printing $0.00 for an absent total, so the
                    absent case says so instead. Populating it needs a new field on
                    the endpoint — a separate change, in the API repo. */}
                {invoices === null ? (
                  <p className="py-4 text-center text-sm text-fieldiq-muted">
                    Invoice detail is not available from this report
                    {summary?.total_invoices ? ` (${summary.total_invoices} invoice${summary.total_invoices === 1 ? '' : 's'} in this period)` : ''}.
                  </p>
                ) : invoices.length === 0 ? (
                  <p className="py-4 text-center text-sm text-fieldiq-muted">No invoices this period.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[620px] text-left text-sm">
                      <thead>
                        <tr className="border-b border-fieldiq-border">
                          <th scope="col" className="px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Date</th>
                          <th scope="col" className="px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Invoice #</th>
                          <th scope="col" className="px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Type</th>
                          <th scope="col" className="px-3 py-2 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Amount</th>
                          <th scope="col" className="px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv: any, i: number) => (
                          <tr key={i} className="border-b border-fieldiq-border last:border-b-0">
                            <td className="px-3 py-2 text-fieldiq-muted">{inv.date}</td>
                            <td className="px-3 py-2 font-medium text-fieldiq-text">{inv.invoice_number}</td>
                            <td className="px-3 py-2"><Badge label={inv.type || 'Trucking'} variant={inv.type?.toLowerCase() === 'rental' ? 'rental' : 'trucking'} /></td>
                            <td className="px-3 py-2 text-right tabular-nums text-fieldiq-text">{inv.amount == null ? '—' : fmt(inv.amount)}</td>
                            <td className="px-3 py-2"><Badge label={inv.status || 'Pending'} variant={statusVariant(inv.status)} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            </>
          )}

          {reportLoading && selectedCustomerId && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-12 text-center">
              <span
                role="status"
                aria-label="Loading this customer's report"
                className="mx-auto block h-7 w-40 animate-pulse rounded-fi-control bg-fieldiq-border"
              />
            </div>
          )}

          {/* A customer is picked, the load finished, nothing failed, and the
              server returned no report (data: null). Without this the page below
              the picker just goes blank, which reads as "no activity" rather
              than "nothing was returned". */}
          {selectedCustomerId && !reportLoading && !reportFailure && !report && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-12 text-center">
              <p className="text-sm font-semibold text-fieldiq-text">No report available</p>
              <p className="mt-1 text-xs text-fieldiq-muted">
                This customer's report could not be loaded for the selected dates.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
